import * as PopoverPrimitive from '@radix-ui/react-popover';
import { forwardRef, type ComponentPropsWithoutRef, type ReactElement, type ReactNode } from 'react';

import { cn } from '@/lib/cn';

/**
 * Popover — a floating panel anchored to a trigger.
 *
 * Portalled, focus-trapped while open (Radix), closes on Esc and outside click, and
 * restores focus to the trigger. Sized to the available viewport height so a long list
 * scrolls inside the panel rather than pushing the page.
 */

export type PopoverSide = 'top' | 'right' | 'bottom' | 'left';
export type PopoverAlign = 'start' | 'center' | 'end';

export const popoverSurfaceClass = cn(
  'z-[var(--z-popover)]',
  'rounded-[var(--radius-lg)] border border-line bg-[var(--surface-overlay)]',
  'text-primary shadow-[var(--shadow-4)]',
  'origin-[var(--radix-popover-content-transform-origin)]',
  'data-[state=open]:animate-[af-scale-in_var(--dur-fast)_var(--ease-out)]',
  'data-[state=closed]:animate-[af-fade-out_var(--dur-instant)_var(--ease-out)]',
);

export interface PopoverProps {
  /** The anchor. Must forward a ref (any of our buttons does). */
  trigger: ReactElement;
  children: ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  side?: PopoverSide;
  align?: PopoverAlign;
  sideOffset?: number;
  alignOffset?: number;
  /** Blocks interaction with the page behind. Default false — popovers are lightweight. */
  modal?: boolean;
  /** Fixed panel width; omit to size to content. */
  width?: number | string;
  /** Applies the standard 4px inset used by list-style popovers. */
  padded?: boolean;
  className?: string;
  /** Accessible name when the panel has no visible heading. */
  label?: string;
  onCloseAutoFocus?: (event: Event) => void;
}

export function Popover({
  trigger,
  children,
  open,
  defaultOpen,
  onOpenChange,
  side = 'bottom',
  align = 'start',
  sideOffset = 6,
  alignOffset = 0,
  modal = false,
  width,
  padded = false,
  className,
  label,
  onCloseAutoFocus,
}: PopoverProps) {
  return (
    <PopoverPrimitive.Root
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      modal={modal}
    >
      <PopoverPrimitive.Trigger asChild>{trigger}</PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side={side}
          align={align}
          sideOffset={sideOffset}
          alignOffset={alignOffset}
          collisionPadding={8}
          aria-label={label}
          onCloseAutoFocus={onCloseAutoFocus}
          style={width === undefined ? undefined : { width }}
          className={cn(
            popoverSurfaceClass,
            'flex max-h-[var(--radix-popover-content-available-height)] flex-col overflow-hidden',
            padded && 'p-1',
            className,
          )}
        >
          {children}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

/** Header strip for popovers that need a title + trailing control. */
export const PopoverHeader = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>(
  function PopoverHeader({ className, ...rest }, ref) {
    return (
      <div
        ref={ref}
        className={cn(
          'flex h-[var(--h-control-lg)] shrink-0 items-center gap-2 border-b border-line-subtle px-3',
          'text-sm font-medium text-secondary',
          className,
        )}
        {...rest}
      />
    );
  },
);

/** Footer strip — reset / apply actions. */
export const PopoverFooter = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>(
  function PopoverFooter({ className, ...rest }, ref) {
    return (
      <div
        ref={ref}
        className={cn(
          'flex shrink-0 items-center justify-between gap-2 border-t border-line-subtle bg-[var(--surface-2)] p-2',
          className,
        )}
        {...rest}
      />
    );
  },
);

export const PopoverRoot = PopoverPrimitive.Root;
export const PopoverTrigger = PopoverPrimitive.Trigger;
export const PopoverAnchor = PopoverPrimitive.Anchor;
export const PopoverPortal = PopoverPrimitive.Portal;
export const PopoverClose = PopoverPrimitive.Close;

export const PopoverContent = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(function PopoverContent({ className, ...rest }, ref) {
  return (
    <PopoverPrimitive.Content
      ref={ref}
      collisionPadding={8}
      className={cn(
        popoverSurfaceClass,
        'flex max-h-[var(--radix-popover-content-available-height)] flex-col overflow-hidden',
        className,
      )}
      {...rest}
    />
  );
});
